export const MARKETING_SMS_COLUMNS = [
  { id: 'created_at', label: 'Date', sortable: true, sortType: 'date', align: 'left', first: true, alwaysVisible: true },
  { id: 'name', label: 'Name', sortable: true, sortType: 'text', align: 'left', alwaysVisible: true },
  { id: 'source', label: 'Source', sortable: true, sortType: 'text', align: 'left' },
  { id: 'venue_name', label: 'Venue', sortable: true, sortType: 'text', align: 'left' },
  { id: 'sent', label: 'Sent', sortable: true, sortType: 'number', align: 'right' },
  { id: 'delivered', label: 'Delivered', sortable: true, sortType: 'number', align: 'right' },
  { id: 'delivery_rate', label: 'Delivery Rate', sortable: true, sortType: 'number', align: 'right' },
  { id: 'clicked', label: 'Clicked', sortable: true, sortType: 'number', align: 'right' },
  { id: 'click_rate', label: 'Click Rate', sortable: true, sortType: 'number', align: 'right' },
];

export const SMS_SORT_TYPES = {
  text: { asc: 'A \u2192 Z', desc: 'Z \u2192 A' },
  number: { asc: 'Low \u2192 High', desc: 'High \u2192 Low' },
  date: { asc: 'Oldest first', desc: 'Newest first' },
};

const SOURCE_COLORS = {
  Broadcast: { background: '#f9f5ff', color: '#6941c6', border: '1px solid #d6bbfb' },
  Workflow: { background: '#fffaeb', color: '#b54708', border: '1px solid #fedf89' },
};

export const SMS_CONFIG = {
  sidebarKey: 'marketing_sms',
  pageTitle: 'SMS',
  pageSubtitle: 'Overview of your SMS marketing performance.',
  defaultPreset: 'year_to_date',
  filterTitle: 'Filter SMS',
  filterSubtitle: 'Apply filters to narrow SMS data.',
  apiEndpoint: (teamId) => `/teams/${teamId}/reporting/marketing-sms/data.json`,

  columns: MARKETING_SMS_COLUMNS,
  sortTypes: SMS_SORT_TYPES,
  defaultSort: { field: 'created_at', direction: 'desc' },
  localStorageKey: (teamId) => `reportingMarketingSmsColumns_${teamId}`,
  nameColumn: 'name',

  kpiColumns: 3,
  buildKpiCards: (kpis, prior, compactNumber) => [
    {
      top: { label: 'SMS Sent', value: compactNumber(kpis.sms_sent || 0), current: kpis.sms_sent, prior: prior.sms_sent, tooltip: 'Total SMS messages sent in the period' },
      bottom: { label: 'SMS Delivered', value: compactNumber(kpis.sms_delivered || 0), current: kpis.sms_delivered, prior: prior.sms_delivered, tooltip: 'Total SMS messages delivered in the period' },
    },
    {
      top: { label: 'Delivery Rate', value: `${kpis.delivery_rate || 0}%`, current: kpis.delivery_rate, prior: prior.delivery_rate, tooltip: 'Percentage of sent SMS that were delivered' },
      bottom: { label: 'Click Rate', value: `${kpis.click_rate || 0}%`, current: kpis.click_rate, prior: prior.click_rate, tooltip: 'Percentage of delivered SMS that were clicked' },
    },
    {
      top: { label: 'SMS Clicked', value: compactNumber(kpis.sms_clicked || 0), current: kpis.sms_clicked, prior: prior.sms_clicked, tooltip: 'Total SMS link clicks in the period' },
      bottom: {
        label: 'Click-to-Delivery Rate',
        value: kpis.sms_delivered > 0 ? `${((kpis.sms_clicked / kpis.sms_delivered) * 100).toFixed(1)}%` : '0%',
        current: kpis.sms_delivered > 0 ? ((kpis.sms_clicked / kpis.sms_delivered) * 100) : 0,
        prior: prior.sms_delivered > 0 ? ((prior.sms_clicked / prior.sms_delivered) * 100) : 0,
        tooltip: 'Percentage of delivered SMS that were clicked',
      },
    },
  ],

  engagementTitle: 'SMS Engagement Over Time',
  engagementSubtitle: 'Delivery rate and click rate trends',
  engagementSeries: [
    { dataKey: 'delivery_rate', label: 'Delivery Rate', color: '#8AC926' },
    { dataKey: 'click_rate', label: 'Click Rate', color: '#6A4C93' },
  ],
  engagementYFormatter: (v) => `${v}%`,

  secondChartTitle: 'SMS Funnel',
  secondChartSubtitle: 'Sent vs delivered vs clicked',
  buildFunnelData: (kpis) => [
    { name: 'Sent', value: kpis.sms_sent || 0 },
    { name: 'Delivered', value: kpis.sms_delivered || 0 },
    { name: 'Clicked', value: kpis.sms_clicked || 0 },
  ],
  funnelHasData: (kpis) => (kpis.sms_sent || 0) > 0,
  funnelLabel: 'SMS',
  funnelGradients: [
    { id: 'smsFunnelGrad0', from: '#8AC926', to: '#C5E99B' },
    { id: 'smsFunnelGrad1', from: '#1982C4', to: '#8EC5E8' },
    { id: 'smsFunnelGrad2', from: '#6A4C93', to: '#B8A9D4' },
  ],

  topPerformersTitle: 'Top Performing Messages',
  topPerformersSubtitle: 'Ranked by delivery rate',
  buildTopPerformers: (rows) =>
    [...rows]
      .filter((r) => r.delivered > 0 && r.delivery_rate != null)
      .sort((a, b) => (b.delivery_rate || 0) - (a.delivery_rate || 0))
      .slice(0, 10),
  topPerformersSeries: [
    { dataKey: 'delivery_rate', label: 'Delivery Rate', color: '#8AC926' },
    { dataKey: 'click_rate', label: 'Click Rate', color: '#6A4C93' },
  ],
  topPerformersYFormatter: (v) => `${v}%`,

  tableTitle: 'Messages',
  searchPlaceholder: 'Search messages...',
  emptyMessage: 'No SMS message data for the selected period.',
  emptySearchMessage: 'No messages match your search.',
  paginationLabel: 'messages',
  csvFilename: 'marketing_sms_messages.csv',

  searchFilter: (r, q) =>
    (r.name || '').toLowerCase().includes(q) ||
    (r.source || '').toLowerCase().includes(q) ||
    (r.venue_name || '').toLowerCase().includes(q),

  renderCellValue: (colId, row, formatDate) => {
    switch (colId) {
      case 'created_at': return formatDate(row.created_at);
      case 'name': return <div style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name || '\u2014'}</div>;
      case 'source': {
        const style = SOURCE_COLORS[row.source] || { background: '#f2f4f7', color: '#344054', border: '1px solid #e4e7ec' };
        return <span className="badge" style={{ ...style, fontSize: 12, fontWeight: 500, padding: '2px 8px', borderRadius: 12 }}>{row.source}</span>;
      }
      case 'venue_name': return <div style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.venue_name || '\u2014'}</div>;
      case 'sent': return (row.sent || 0).toLocaleString();
      case 'delivered': return (row.delivered || 0).toLocaleString();
      case 'delivery_rate': return row.delivery_rate != null ? `${row.delivery_rate}%` : '\u2014';
      case 'clicked': return (row.clicked || 0).toLocaleString();
      case 'click_rate': return row.click_rate != null ? `${row.click_rate}%` : '\u2014';
      default: return '\u2014';
    }
  },

  buildCsvValue: (colId, row) => {
    switch (colId) {
      case 'created_at': return row.created_at ? new Date(row.created_at).toLocaleDateString() : '';
      case 'name': return row.name || '';
      case 'source': return row.source || '';
      case 'venue_name': return row.venue_name || '';
      case 'sent': return row.sent || 0;
      case 'delivered': return row.delivered || 0;
      case 'delivery_rate': return row.delivery_rate != null ? row.delivery_rate : '';
      case 'clicked': return row.clicked || 0;
      case 'click_rate': return row.click_rate != null ? row.click_rate : '';
      default: return '';
    }
  },

  buildTotals: (totals) => ({
    sent: (totals.sent || 0).toLocaleString(),
    delivered: (totals.delivered || 0).toLocaleString(),
    delivery_rate: `${totals.delivery_rate || 0}%`,
    clicked: (totals.clicked || 0).toLocaleString(),
    click_rate: `${totals.click_rate || 0}%`,
  }),
};
